import React, { useEffect } from 'react';
import { Row, Col, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import Select from 'react-select';
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from 'react-redux';
import { selectPostcodes, updateDeliveryDetails, selectBookingFormData, selectDeliveryDetails, setSelectedPostcode, selectSelectedPostcode } from 'slices/booking/bookingSlice';
import { selectIsDiscountedBooking, selectIsEdit } from 'slices/booking/cartSlice';

interface FormValues {
  venue: string;
  address_line_1: string;
  address_line_2: string;
  town: string;
  complete_postcode: string;
  postcode_id: number;
  sameAsCustomer: boolean;
}

function DeliveryDetailsSection() {
  const dispatch = useDispatch();
  const postcodes = useSelector(selectPostcodes);
  const customerDetails = useSelector(selectBookingFormData);
  const deliveryDetails = useSelector(selectDeliveryDetails);
  const isEdit = useSelector(selectIsEdit);
  const isDiscountedBooking = useSelector(selectIsDiscountedBooking); 
  const userSelectedPostcode = useSelector(selectSelectedPostcode);
  

  const formik = useFormik<FormValues>({
    initialValues: isEdit ? {
      venue: deliveryDetails.venue || '',
      address_line_1: deliveryDetails.address_line_1 || '',
      address_line_2: deliveryDetails.address_line_2 || '',
      town: deliveryDetails.town || '',
      complete_postcode: deliveryDetails.complete_postcode || '',
      postcode_id: deliveryDetails.postcode_id || 0,
      sameAsCustomer: false
    } : 
    {
      venue: '',
      address_line_1: '',
      address_line_2: '',
      town: '',
      complete_postcode: '',
      postcode_id: 0,
      sameAsCustomer: false
    },
    validationSchema: yup.object().shape({
      venue: yup.string().required('Please enter venue name'),
      address_line_1: yup.string().required('Please enter address line 1'),
      address_line_2: yup.string(),
      town: yup.string().required('Please enter town'),
      complete_postcode: yup.string().required('Please enter complete postcode'),
      postcode_id: yup.number().required('Please select a postcode'),
      sameAsCustomer: yup.boolean()
    }),
    onSubmit: values => {
      // Submit logic here
    },
  });

  // Update delivery details in Redux whenever values change
  useEffect(() => {
    const deliveryDetails = {
      venue: formik.values.venue,
      address_line_1: formik.values.address_line_1,
      address_line_2: formik.values.address_line_2,
      complete_postcode: formik.values.complete_postcode,
      town: formik.values.town,
      postcode_id: formik.values.postcode_id || undefined
    };
    dispatch(updateDeliveryDetails(deliveryDetails));
    const selectedPostcode = postcodes.find(
      (postcode) => postcode.id === formik.values.postcode_id,
    );
    if (selectedPostcode) {
      dispatch(
      setSelectedPostcode({
        postcode: (selectedPostcode.id).toString(),
        town: selectedPostcode?.town || '',
        region: selectedPostcode?.region || '',
        charges: isEdit ?  userSelectedPostcode.charges : Number(selectedPostcode?.charges) || 0,
      }),
    );
    }
  }, [dispatch, formik.values]);

  // Handle checkbox change
  const handleSameAsCustomer = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    formik.setFieldValue('sameAsCustomer', isChecked);
  };

  // Update address fields when checkbox is changed or customer details update
  useEffect(() => {
    if (customerDetails && formik.values.sameAsCustomer) {
      formik.setValues({
        ...formik.values,
        address_line_1: customerDetails.address_line_1,
        address_line_2: customerDetails.address_line_2,
        town: customerDetails.town,
        complete_postcode: customerDetails.complete_postcode,
        postcode_id: isEdit ? deliveryDetails.postcode_id || 0 : customerDetails.postcode_id || 0
      });
    }
  }, [customerDetails, formik.values.sameAsCustomer]);

  return (
    <form>
      <Row>
        <Col md={12}>
          <FormGroup check className="mb-3">
            <Input
              type="checkbox"
              id="sameAsCustomer"
              name="sameAsCustomer"
              onChange={handleSameAsCustomer}
              checked={formik.values.sameAsCustomer}
            />
            <Label check for="sameAsCustomer">
              Same as customer address
            </Label>
          </FormGroup>
        </Col>

        <Col md={12}>
          <FormGroup className="form-group">
            <Label for="venue">Venue Name: <span className="text-danger">*</span></Label>
            <Input
              id="venue"
              name="venue"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.venue}
              invalid={formik.touched.venue && formik.errors.venue ? true : false}
            />
            {formik.touched.venue && formik.errors.venue && (
              <FormFeedback className="error-message">{formik.errors.venue}</FormFeedback>
            )}
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup className="form-group">
            <Label for="postcode_id">Area code: <span className="text-danger">*</span></Label>
            <Select
              placeholder=""
              className={
                formik.touched.postcode_id && formik.errors.postcode_id
                  ? "is-invalid"
                  : ""
              }
              options={postcodes.map(postcode => ({
                value: postcode.id.toString(),
                label: `${postcode.postcode} - ${postcode.charges}`
              }))}
              value={postcodes
                .map(postcode => ({
                  value: postcode.id.toString(),
                  label: `${postcode.postcode} - ${postcode.charges}`
                }))
                .find(option => option.value === formik.values.postcode_id?.toString())
              }
              onChange={(selectedOption) => {
                if (selectedOption) {
                  const selectedPostcode = postcodes.find(p => p.id.toString() === selectedOption.value);
                  if (selectedPostcode) {
                    formik.setValues({
                      ...formik.values,
                      postcode_id: parseInt(selectedOption.value),
                      town: selectedPostcode.town,
                      address_line_2: selectedPostcode.region,
                      complete_postcode: selectedPostcode.postcode + "-"
                    });
                  }
                }
              }}
              isDisabled={formik.values.sameAsCustomer || isDiscountedBooking || isEdit}
            />
            {formik.touched.postcode_id && formik.errors.postcode_id && (
              <FormFeedback className="error-message">{formik.errors.postcode_id}</FormFeedback>
            )}
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup className="form-group">
            <Label for="complete_postcode">Postcode: <span className="text-danger">*</span></Label>
            <Input
              id="complete_postcode"
              name="complete_postcode"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.complete_postcode}
              invalid={!!(formik.touched.complete_postcode && formik.errors.complete_postcode)}
              disabled={formik.values.sameAsCustomer}
            />
            {formik.touched.complete_postcode && formik.errors.complete_postcode && (
              <FormFeedback className="error-message">{formik.errors.complete_postcode}</FormFeedback>
            )}
          </FormGroup>
        </Col>

        <Col md={12}>
          <FormGroup className="form-group">
            <Label for="address_line_1">Address Line 1: <span className="text-danger">*</span></Label>
            <Input
              id="address_line_1"
              name="address_line_1"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.address_line_1}
              invalid={!!(formik.touched.address_line_1 && formik.errors.address_line_1)}
              disabled={formik.values.sameAsCustomer}
            />
            {formik.touched.address_line_1 && formik.errors.address_line_1 && (
              <FormFeedback className="error-message">{formik.errors.address_line_1}</FormFeedback>
            )}
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup className="form-group">
            <Label for="address_line_2">Address Line 2:</Label>
            <Input
              id="address_line_2"
              name="address_line_2"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.address_line_2}
              disabled={formik.values.sameAsCustomer}
            />
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup className="form-group">
            <Label for="town">Town: <span className="text-danger">*</span></Label>
            <Input
              id="town"
              name="town"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.town}
              invalid={!!(formik.touched.town && formik.errors.town)}
              disabled={formik.values.sameAsCustomer}
            />
            {formik.touched.town && formik.errors.town && (
              <FormFeedback className="error-message">{formik.errors.town}</FormFeedback>
            )}
          </FormGroup>
        </Col>
      </Row>
    </form>
  );
}

export default DeliveryDetailsSection;
